import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Icon, cn } from "@nexus/ui";
import type { IconName } from "@nexus/ui";
import { scoreToBand } from "../../lib/score-band";
import type { ScoredAsset } from "../../lib/score-clusters";
import { AssetNameLabel } from "../AssetNameLabel/AssetNameLabel";
import { AssetThumbnail } from "../AssetThumbnail/AssetThumbnail";
import { BandPill } from "../BandPill/BandPill";

/** Rows are tap targets, so a row keeps the 44px minimum: 28px thumbnail + 8px padding. */
const ROW_THUMB_HEIGHT = "h-7";

/** Height of the preview strip when a creative has no thumbnail to show. */
const FALLBACK_PREVIEW_HEIGHT = "h-14";

export interface RailClusterDetailsProps {
  /** Rounded score shared by every creative in the cluster. */
  score: number;
  assets: readonly ScoredAsset[];
  /** 1-based rank of this score among the plotted creatives. */
  rank: number;
  total: number;
  /** Points above (positive) or below (negative) the average score in scope. */
  deltaFromAverage: number;
  /** Creative the pointer or keyboard is currently on — highlighted in the list. */
  activeId: string | null;
  /** Whether picking a creative goes anywhere — false for the market rail, which has no cards. */
  isSelectable: boolean;
  onActivateAsset: (assetId: string) => void;
  onSelectAsset: (assetId: string) => void;
}

/**
 * What sits inside the rail's hover panel: the score in context (band, rank, distance from
 * the average) plus the creative behind the dot — or, for a cluster, every creative sharing
 * that score as a pickable list.
 */
export function RailClusterDetails({
  score,
  assets,
  rank,
  total,
  deltaFromAverage,
  activeId,
  isSelectable,
  onActivateAsset,
  onSelectAsset,
}: RailClusterDetailsProps) {
  const { t } = useTranslation();
  const lead = assets[0];
  const band = lead.band ?? scoreToBand(score);
  const isCluster = assets.length > 1;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 px-3 pt-2.5">
        <span className="text-foreground text-sm font-semibold tabular-nums">
          {t("preflight.rail.hoverScore", { score })}
        </span>
        <BandPill band={band} className="ml-auto" />
      </div>

      <p className="text-muted-foreground flex items-center gap-1 px-3 pt-1 pb-2.5 text-[11px]">
        <span className="tabular-nums">
          {t("preflight.rail.hoverRank", { rank, total })}
        </span>
        <span aria-hidden>·</span>
        <Icon
          name={deltaIcon(deltaFromAverage)}
          size="xs"
          className="shrink-0"
          aria-hidden
        />
        <span className="tabular-nums">{deltaLabel(t, deltaFromAverage)}</span>
      </p>

      {isCluster ? (
        <>
          <p className="text-muted-foreground border-border/60 border-t px-3 py-1.5 text-[10px] font-medium tracking-wide uppercase">
            {t("preflight.rail.hoverClusterHeading", { count: assets.length })}
          </p>
          <ul className="border-border/60 flex max-h-52 flex-col overflow-y-auto border-t">
            {assets.map((asset) => (
              <li key={asset.id}>
                <button
                  type="button"
                  aria-label={t("preflight.rail.dotLabel", {
                    asset: asset.label,
                    score,
                  })}
                  className={cn(
                    "hover:bg-muted/70 focus-visible:ring-ring/60 flex w-full items-center gap-2 px-2.5 py-2 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-inset motion-reduce:transition-none",
                    isSelectable ? "cursor-pointer" : "cursor-default",
                    asset.id === activeId && "bg-muted/70",
                  )}
                  onPointerEnter={() => onActivateAsset(asset.id)}
                  onFocus={() => onActivateAsset(asset.id)}
                  onClick={
                    isSelectable ? () => onSelectAsset(asset.id) : undefined
                  }
                >
                  <AssetThumbnail
                    thumbnailUrl={asset.thumbnailUrl}
                    isVideo={asset.isVideo}
                    iconSize="xs"
                    className={cn("w-11 shrink-0 rounded-sm", ROW_THUMB_HEIGHT)}
                  />
                  <AssetNameLabel
                    name={asset.label}
                    className="text-foreground min-w-0 flex-1 text-xs"
                  />
                  {isSelectable ? (
                    <Icon
                      name="chevron-right"
                      size="xs"
                      className="text-muted-foreground shrink-0"
                      aria-hidden
                    />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
          {isSelectable ? (
            <p className="text-muted-foreground border-border/60 border-t px-3 py-1.5 text-[11px]">
              {t("preflight.rail.hoverSelectHint")}
            </p>
          ) : null}
        </>
      ) : (
        <>
          <AssetThumbnail
            thumbnailUrl={lead.thumbnailUrl}
            assetUrl={lead.assetUrl}
            isVideo={lead.isVideo}
            autoPlay
            iconSize="md"
            className="w-full"
            imageClassName="aspect-video"
            // A bare fallback glyph doesn't need a full 16:9 frame of empty grey.
            fallbackClassName={FALLBACK_PREVIEW_HEIGHT}
          />
          <div className="flex flex-col gap-0.5 px-3 py-2">
            <AssetNameLabel
              name={lead.label}
              className="text-foreground text-xs font-medium"
            />
            {isSelectable ? (
              <span className="text-muted-foreground text-[11px]">
                {t("preflight.rail.hoverOpenHint")}
              </span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/** Direction glyph so the distance from the average isn't carried by wording alone. */
function deltaIcon(delta: number): IconName {
  const rounded = Math.round(delta);
  if (rounded > 0) return "chevron-up";
  if (rounded < 0) return "chevron-down";
  return "minus";
}

function deltaLabel(t: TFunction, delta: number): string {
  const rounded = Math.round(delta);
  if (rounded === 0) return t("preflight.rail.hoverAtAverage");
  const key =
    rounded > 0
      ? "preflight.rail.hoverAboveAverage"
      : "preflight.rail.hoverBelowAverage";
  return t(key, { delta: Math.abs(rounded) });
}
